'use client';

import { CheckIcon } from '@heroicons/react/20/solid';
import { ArrowUpRightIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { ReactNode, useEffect, useState } from 'react';
import ModalForm from '../Modal/Modal';
import { SerializedEditorState } from '@payloadcms/richtext-lexical/lexical';
import CustomRichText from '../CustomRichText';
import { useCurrentLocale } from '@/utils/hooks/useCurrentLocale';
import { localeText } from '@/utils/constantTxt/localeText';

const defaultTiers = [
  {
    name: 'базовий',
    id: 'tier-base',
    href: '#',
    price: {
      monthly: '1 900',
    },
    description: 'The essentials to provide your best work for clients.',
    features: [
      { name: 'Свідоцтво про базову середню освіту державного зразка', enters: true },
      { name: 'Доступ до навчальної платформи 24/7', enters: true },
      { name: 'Авторські навчальні матеріали', enters: true },
      { name: 'Індивідуальний звіт про успішність дитини', enters: true },
      { name: 'Загальна підтримка від куратора класу', enters: true },
      { name: '*Понад 1000 авторських уроків', enters: true },
      {
        name: 'Понад тисячу факультативних уроків (ІТ, маркетинг, лідерство, бізнес-менеджемент тощо)',
        enters: true,
      },
      { name: 'Записи уроків у форматі Close to You', enters: true },
      { name: 'VR Окуляри', enters: true },
      { name: 'Уроки в живому форматі', enters: false },
      {
        name: 'Індивідуальний графік уроків відповідно до побажань батьків та дитини',
        enters: false,
      },
      { name: 'Мовні практики з викладачем у групі до 6 дітей 3 рази/тиждень', enters: false },
      { name: 'Клуби за інтересами', enters: false },
      { name: 'Ноутбук у використання', enters: false },
      { name: 'Підготовка до НМТ (записи, теоретичні)', enters: false },
      { name: 'AP note зошит', enters: false },
      { name: 'Технологія Kinect', enters: false },
      { name: 'Індиідуальні консультації з психологом', enters: false },
      { name: 'Письмова гарантія вступу в університет без жодних репетиторів', enters: false },
      {
        name: 'Підбір ВНЗ (в Україні і за кордоном), підготовка до вступу, повний супровід у вступній кампанії',
        enters: false,
      },
      { name: 'Зустрічі із топ-спеціалістами різних професій за інтересами учня', enters: false },
    ],
    superPrice: false,
  },
  {
    name: 'стандарт',
    id: 'tier-standart',
    href: '#',
    price: {
      monthly: '5 500',
    },
    description: 'A plan that scales with your rapidly growing business.',
    features: [
      { name: 'Свідоцтво про базову середню освіту державного зразка', enters: true },
      { name: 'Доступ до навчальної платформи 24/7', enters: true },
      { name: 'Авторські навчальні матеріали', enters: true },
      { name: 'Індивідуальний звіт про успішність дитини', enters: true },
      { name: 'Загальна підтримка від куратора класу', enters: true },
      { name: '*Понад 1000 авторських уроків', enters: true },
      {
        name: 'Понад тисячу факультативних уроків (ІТ, маркетинг, лідерство, бізнес-менеджемент тощо)',
        enters: true,
      },
      { name: 'Записи уроків у форматі Close to You', enters: true },
      { name: 'VR Окуляри', enters: true },
      { name: 'Уроки в живому форматі', enters: true },
      {
        name: 'Індивідуальний графік уроків відповідно до побажань батьків та дитини',
        enters: true,
      },
      { name: 'Мовні практики з викладачем у групі до 6 дітей 3 рази/тиждень', enters: true },
      { name: 'Клуби за інтересами', enters: true },
      { name: 'Ноутбук у використання', enters: true },
      { name: 'Підготовка до НМТ (записи, теоретичні)', enters: true },
      { name: 'AP note зошит', enters: false },
      { name: 'Технологія Kinect', enters: false },
      { name: 'Індиідуальні консультації з психологом', enters: false },
      { name: 'Письмова гарантія вступу в університет без жодних репетиторів', enters: false },
      {
        name: 'Підбір ВНЗ (в Україні і за кордоном), підготовка до вступу, повний супровід у вступній кампанії',
        enters: false,
      },
      { name: 'Зустрічі із топ-спеціалістами різних професій за інтересами учня', enters: false },
    ],
    superPrice: false,
  },
  {
    name: 'топ',
    id: 'tier-proff',
    href: '#',
    price: {
      monthly: '10 500',
    },
    description: 'Dedicated support and infrastructure for your company.',
    features: [
      { name: 'Свідоцтво про базову середню освіту державного зразка', enters: true },
      { name: 'Доступ до навчальної платформи 24/7', enters: true },
      { name: 'Авторські навчальні матеріали', enters: true },
      { name: 'Індивідуальний звіт про успішність дитини', enters: true },
      { name: 'Загальна підтримка від куратора класу', enters: true },
      { name: '*Понад 1000 авторських уроків', enters: true },
      {
        name: 'Понад тисячу факультативних уроків (ІТ, маркетинг, лідерство, бізнес-менеджемент тощо)',
        enters: true,
      },
      { name: 'Записи уроків у форматі Close to You', enters: true },
      { name: 'VR Окуляри', enters: true },
      { name: 'Уроки в живому форматі', enters: true },
      {
        name: 'Індивідуальний графік уроків відповідно до побажань батьків та дитини',
        enters: true,
      },
      { name: 'Мовні практики з викладачем у групі до 6 дітей 3 рази/тиждень', enters: true },
      { name: 'Клуби за інтересами', enters: true },
      { name: 'Ноутбук у використання', enters: true },
      { name: 'Підготовка до НМТ (записи, теоретичні)', enters: true },
      { name: 'AP note зошит', enters: true },
      { name: 'Технологія Kinect', enters: true },
      { name: 'Індиідуальні консультації з психологом', enters: true },
      { name: 'Письмова гарантія вступу в університет без жодних репетиторів', enters: true },
      {
        name: 'Підбір ВНЗ (в Україні і за кордоном), підготовка до вступу, повний супровід у вступній кампанії',
        enters: true,
      },
      { name: 'Зустрічі із топ-спеціалістами різних професій за інтересами учня', enters: true },
    ],
    superPrice: false,
  },
];

type PriceType = {
  monthly: string;
};

type FeatureType = {
  name: ReactNode | SerializedEditorState;
  enters: boolean;
};

type PlanType = {
  name: string;
  id: string;
  href: string;
  price: PriceType;
  description: ReactNode | SerializedEditorState;
  features: FeatureType[];
  superPrice: boolean;
};

export interface PriceBlockSchoolProps {
  id?: string;
  title: ReactNode | SerializedEditorState;
  subtitle: ReactNode | SerializedEditorState;
  selectPackageLabel: string;
  viewPackageLabel: string;
  hideFeaturesLabel: string;
  showFeaturesLabel: string;
  modalTagPrefix: string;
  useDefaultTires?: boolean;
  tiers?: PlanType[];
}

export default function PriceBlockSchool({
  id,
  title,
  subtitle,
  selectPackageLabel,
  viewPackageLabel,
  hideFeaturesLabel,
  showFeaturesLabel,
  modalTagPrefix,
  useDefaultTires = true,
  tiers,
}: PriceBlockSchoolProps) {
  const loc = useCurrentLocale();
  const priceSuffix = localeText[loc].uahMonth;

  const [dispayedTiers] = useState(useDefaultTires || !tiers?.length ? defaultTiers : tiers);
  const [showAllFeatures, setShowAllFeatures] = useState(false);
  const [selectedTier, setSelectedTier] = useState<PlanType | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState<PlanType | null>(null);

  const openForm = () => setIsFormOpen(true);
  const closeForm = () => setIsFormOpen(false);

  const getAnnualPrice = (monthlyPrice: string): string => {
    const numeric = parseInt(monthlyPrice.replace(/\s/g, ''), 10);
    const annual = numeric * 10;
    return annual.toLocaleString('uk-UA');
  };

  const selectPackage = (tier: PlanType) => {
    setSelectedPackage(tier);
    openForm();
  };

  const toggleFeatures = () => {
    setShowAllFeatures(!showAllFeatures);
  };

  useEffect(() => {
    const hashId = window?.document.location.hash.slice(1);
    if (id === hashId) {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [id]);

  return (
    <>
      <div id={id} data-meta="PriceBlockSchool" className="py-14">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-4xl text-center">
            <CustomRichText
              data={title}
              tag="div"
              className="mt-2 text-4xl font-bold tracking-tight text-white sm:text-5xl"
            />
          </div>
          <CustomRichText
            data={subtitle}
            tag="div"
            className="mx-auto mt-6 max-w-7xl text-center leading-8 text-white"
          />
          <div className="isolate mx-auto mt-10 grid max-w-md grid-cols-1 gap-8 lg:mx-0 lg:max-w-none lg:grid-cols-3">
            {dispayedTiers.map(tier => (
              <div
                key={tier.id}
                className={`${
                  tier.superPrice ? 'ring-2 ring-indigo-600' : 'ring-1 ring-gray-200'
                } rounded-3xl p-4 lg:p-4 justify-between bg-white`}
              >
                <div className="flex lg:flex-col max-lg:gap-2 max-lg:justify-between border-b-2 pb-4 border-violet-200">
                  <div className="flex items-center justify-between gap-x-4">
                    <p
                      id={tier.id}
                      className="uppercase text-white rounded-full py-1 px-4 bg-custom-btn-gradient-violet text-sm font-semibold lg:leading-6"
                    >
                      {tier.name}
                    </p>
                  </div>
                  <div className="lg:mt-6 flex flex-col gap-y-2">
                    <p className="flex items-baseline gap-x-1">
                      <span className="text-xl lg:text-4xl font-bold tracking-tight text-gray-900">
                        {getAnnualPrice(tier.price.monthly)}
                      </span>
                      <span className="text-sm font-semibold leading-6 text-gray-900">за рік</span>
                    </p>
                    <p className="flex items-baseline gap-x-1">
                      <span className="text-xs font-semibold leading-5 text-gray-500">
                        {tier.price.monthly} {priceSuffix}
                      </span>
                    </p>
                  </div>
                </div>
                <button
                  aria-describedby={tier.id}
                  onClick={() => selectPackage(tier)}
                  className={`${
                    tier.superPrice ? 'bg-gray-900 text-white shadow-sm' : 'text-gray-900'
                  } hidden uppercase mt-6 lg:flex items-center justify-center w-full ring-1 ring-inset ring-violet-500 rounded-full px-1 py-2 text-sm font-bold leading-6 relative`}
                >
                  <span>{selectPackageLabel}</span>
                  <div className="absolute right-1 flex items-center">
                    <div className="rounded-full custom-gradient-violet-btn">
                      <ArrowUpRightIcon className="text-white h-6 w-6 m-1" />
                    </div>
                  </div>
                </button>
                <ul className="hidden lg:block mt-8 space-y-3 text-sm leading-6 text-gray-600 xl:mt-10 transition-all">
                  {(tier.features.length > 5
                    ? tier.features
                        .slice(0, showAllFeatures ? tier.features.length : 3)
                        .concat(
                          !showAllFeatures ? tier.features.slice(tier.features.length - 2) : [],
                        )
                    : tier.features
                  ).map((feature, index) => (
                    <li key={index} className="flex gap-x-3">
                      <CheckIcon
                        aria-hidden="true"
                        className={`${
                          feature.enters
                            ? 'text-white custom-gradient-violet-btn'
                            : 'bg-violet-600/40'
                        } h-6 w-6 flex-none text-white rounded-full`}
                      />
                      <CustomRichText
                        data={feature.name}
                        tag="span"
                        className={`${feature.enters ? 'text-gray-900' : 'text-gray-300'}`}
                      />
                    </li>
                  ))}
                </ul>
                <button
                  onClick={toggleFeatures}
                  className="hidden lg:block p-6 mt-4 text-violet-600 font-semibold"
                >
                  {showAllFeatures ? hideFeaturesLabel : showFeaturesLabel}
                </button>
                <button
                  aria-describedby={tier.id}
                  onClick={() => setSelectedTier(tier)}
                  className={`${
                    tier.superPrice ? 'bg-gray-900 text-white shadow-sm' : 'text-gray-900'
                  } hidden uppercase mt-6 max-lg:flex items-center justify-center w-full ring-1 ring-inset ring-violet-500 rounded-full px-1 py-2 text-sm font-semibold leading-6 relative`}
                >
                  <span>{viewPackageLabel}</span>
                  <div className="absolute right-1 flex items-center">
                    <div className="rounded-full custom-gradient-violet-btn">
                      <ArrowUpRightIcon className="text-white h-6 w-6 m-1" />
                    </div>
                  </div>
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
      {selectedTier && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-start justify-start overflow-y-auto w-full h-lvh">
          <div className="bg-white p-2 rounded-xl">
            <div className="w-full flex justify-end p-2">
              <button onClick={() => setSelectedTier(null)} aria-label="Закрити">
                <XCircleIcon className="h-6 w-6 text-violet-600" />
              </button>
            </div>
            <div className="p-4 lg:p-8 xl:p-10 justify-between bg-white">
              <div className="flex lg:flex-col max-lg:gap-2 max-lg:justify-between">
                <div className="flex items-center justify-between gap-x-4">
                  <p
                    id={selectedTier.id}
                    className="uppercase text-white rounded-full py-1 px-2 bg-custom-btn-gradient-violet text-base font-semibold lg:leading-8"
                  >
                    {selectedTier.name}
                  </p>
                </div>
                <div className="lg:mt-6 flex flex-col gap-y-2">
                  <p className="flex items-baseline gap-x-1">
                    <span className="text-xl lg:text-4xl font-bold tracking-tight text-gray-900">
                      {getAnnualPrice(selectedTier.price.monthly)}
                    </span>
                    <span className="text-sm font-semibold leading-6 text-gray-900">за рік</span>
                  </p>
                  <p className="flex items-baseline gap-x-1">
                    <span className="text-xs font-semibold leading-5 text-gray-500">
                      {selectedTier.price.monthly} {priceSuffix}
                    </span>
                  </p>
                </div>
              </div>
              <button
                aria-describedby={selectedTier.id}
                onClick={() => selectPackage(selectedTier)}
                className={`${
                  selectedTier.superPrice ? 'bg-gray-900 text-white shadow-sm' : 'text-gray-900'
                } uppercase mt-6 lg:flex items-center justify-center w-full ring-1 ring-inset ring-violet-500 rounded-full px-1 py-2 text-sm font-semibold leading-6 relative`}
              >
                <span>{selectPackageLabel}</span>
                <div className="absolute right-1 flex items-center">
                  <div className="rounded-full custom-gradient-violet-btn">
                    <ArrowUpRightIcon className="text-white h-6 w-6 m-1" />
                  </div>
                </div>
              </button>
              <ul className="mt-8 space-y-3 text-sm leading-6 text-gray-600 xl:mt-10 transition-all">
                {selectedTier.features.map((feature, index) => (
                  <li key={index} className="flex gap-x-3">
                    <CheckIcon
                      aria-hidden="true"
                      className={`${
                        feature.enters
                          ? 'text-white custom-gradient-violet-btn'
                          : 'bg-violet-600/40'
                      } h-6 w-6 flex-none text-white rounded-full`}
                    />
                    <CustomRichText
                      data={feature.name}
                      tag="span"
                      className={`${feature.enters ? 'text-gray-900' : 'text-gray-300'}`}
                    />
                  </li>
                ))}
              </ul>
              <button
                onClick={() => setSelectedTier(null)}
                aria-describedby={selectedTier.id}
                className={`${
                  selectedTier.superPrice ? 'bg-gray-900 text-white shadow-sm' : 'text-gray-900'
                } uppercase mt-6 lg:flex items-center justify-center w-full ring-1 ring-inset ring-violet-500 rounded-full px-1 py-2 text-sm font-semibold leading-6 relative`}
              >
                <span>{hideFeaturesLabel}</span>
                <div className="absolute isolate top-1 right-1 flex items-center">
                  <div className="rounded-full custom-gradient-violet-btn">
                    <ArrowUpRightIcon className="text-white h-6 w-6 m-1" />
                  </div>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}
      {isFormOpen && selectedPackage && (
        <ModalForm
          closeForm={closeForm}
          btnMeta={{
            tag: `${modalTagPrefix}-${selectedPackage?.name}`,
          }}
          color={'violet'}
        />
      )}
    </>
  );
}
